import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import { movies } from "@/mocks/movies";
import { categories, ratingOptions } from "@/mocks/categories";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 12;

function splitParam(value: string | null): string[] {
  if (!value) return [];
  return value.split(",").filter(Boolean);
}

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const q = (searchParams.get("q") ?? "").trim();
  const categoryKey = searchParams.get("category") ?? "";
  const selectedGenres = splitParam(searchParams.get("genre"));
  const selectedEras = splitParam(searchParams.get("era"));
  const selectedRatings = splitParam(searchParams.get("rating"));
  const sort = searchParams.get("sort") ?? "rating";
  const rawPage = Number(searchParams.get("page") ?? "1") || 1;

  const activeCategory =
    categories.find((item) => item.key === categoryKey) ?? null;

  const pageTitle = activeCategory
    ? `${activeCategory.name} - 适合夫妻二人看的影片`
    : "影片分类 - 适合夫妻二人看的影片";
  const pageDescription = activeCategory
    ? `${activeCategory.name}推荐：${activeCategory.desc}。精选适合夫妻二人共同观看的${activeCategory.name}，附观影指南与真实短评。`
    : "按类型、年代、评分多维筛选适合夫妻二人共同观看的影片，支持排序与分页，快速找到今晚想一起看的电影。";

  usePageMeta({
    title: pageTitle,
    description: pageDescription,
    keywords: `适合夫妻二人看的影片,${activeCategory ? activeCategory.name : "影片分类"},夫妻电影推荐,情侣电影,观影指南`,
  });

  const updateParams = (
    updates: Record<string, string | null>,
    resetPage = true
  ) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (!value) next.delete(key);
      else next.set(key, value);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const toggleMulti = (key: string, current: string[], value: string) => {
    const next = current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value];
    updateParams({ [key]: next.length ? next.join(",") : null });
  };

  const clearFilters = () => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (categoryKey) next.set("category", categoryKey);
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    const keyword = q.toLowerCase();
    const list = movies.filter((movie) => {
      if (activeCategory && !movie.genres.includes(activeCategory.genre)) {
        return false;
      }
      if (keyword) {
        const haystack = [
          movie.title,
          movie.englishTitle,
          movie.director,
          ...movie.cast,
          ...movie.tags,
          ...movie.genres,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      if (
        selectedGenres.length &&
        !selectedGenres.some((genre) => movie.genres.includes(genre))
      ) {
        return false;
      }
      if (selectedEras.length) {
        const eraMatched = selectedEras.some((era) => {
          if (era === "nearOne") return movie.year >= 2025;
          if (era === "nearThree") return movie.year >= 2023;
          if (era === "classic") return movie.year < 2000;
          return false;
        });
        if (!eraMatched) return false;
      }
      if (selectedRatings.length) {
        const thresholds = selectedRatings.map(
          (key) => ratingOptions.find((item) => item.key === key)?.min ?? 0
        );
        if (movie.rating < Math.min(...thresholds)) return false;
      }
      return true;
    });

    return [...list].sort((a, b) =>
      sort === "year" ? b.year - a.year : b.rating - a.rating
    );
  }, [q, activeCategory, selectedGenres, selectedEras, selectedRatings, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, rawPage), totalPages);
  const paged = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const filterPanel = (
    <FilterPanel
      selectedGenres={selectedGenres}
      selectedEras={selectedEras}
      selectedRatings={selectedRatings}
      onToggleGenre={(value) => toggleMulti("genre", selectedGenres, value)}
      onToggleEra={(value) => toggleMulti("era", selectedEras, value)}
      onToggleRating={(value) => toggleMulti("rating", selectedRatings, value)}
      onClear={clearFilters}
    />
  );

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs md:text-sm text-foreground-600">
          <Link to="/" className="cursor-pointer hover:text-primary-700">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm"></i>
          <span className="text-foreground-600">分类</span>
          {activeCategory && (
            <>
              <i className="ri-arrow-right-s-line text-sm"></i>
              <span className="text-foreground-950 font-medium">
                {activeCategory.name}
              </span>
            </>
          )}
        </nav>

        <div className="mt-5 flex flex-col gap-2">
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-950">
            {q
              ? `“${q}” 的搜索结果`
              : activeCategory
                ? activeCategory.name
                : "全部影片"}
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-foreground-600">
            {q
              ? "已为你匹配片名、主演、导演与标签中包含该关键词的影片，可继续使用筛选进一步缩小范围。"
              : activeCategory
                ? activeCategory.desc
                : "这里汇集了全部精选影片，按类型、年代、评分自由筛选，找到最适合你们的那一部。"}
          </p>
        </div>

        <div className="mt-7 grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-6 lg:gap-8 items-start">
          <aside className="hidden lg:block">
            <div className="sticky top-20">{filterPanel}</div>
          </aside>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200/70 bg-background-50 px-4 py-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-1.5 text-sm text-foreground-700 cursor-pointer"
                >
                  <i className="ri-filter-3-line text-base"></i>
                  筛选
                </button>
                <p className="text-sm text-foreground-600">
                  共{" "}
                  <span className="font-medium text-primary-700">
                    {filtered.length}
                  </span>{" "}
                  部影片
                </p>
              </div>

              <div className="flex items-center gap-2">
                <label
                  htmlFor="sort-select"
                  className="text-sm text-foreground-600"
                >
                  排序
                </label>
                <select
                  id="sort-select"
                  value={sort}
                  onChange={(e) =>
                    updateParams({ sort: e.target.value || null }, false)
                  }
                  className="cursor-pointer rounded-md border border-background-300/70 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none focus:border-primary-400"
                >
                  <option value="rating">评分从高到低</option>
                  <option value="year">上映时间从新到旧</option>
                </select>
              </div>
            </div>

            {paged.length > 0 ? (
              <>
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {paged.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="mt-10 flex items-center justify-center gap-2">
                    <button
                      type="button"
                      disabled={currentPage <= 1}
                      onClick={() =>
                        updateParams({ page: String(currentPage - 1) }, false)
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      <i className="ri-arrow-left-s-line text-lg"></i>
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (num) => (
                        <button
                          key={num}
                          type="button"
                          onClick={() =>
                            updateParams({ page: String(num) }, false)
                          }
                          className={`h-9 min-w-9 rounded-md border px-3 text-sm transition-colors cursor-pointer ${
                            num === currentPage
                              ? "border-primary-500 bg-primary-500 text-background-50"
                              : "border-background-300/70 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                          }`}
                        >
                          {num}
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      disabled={currentPage >= totalPages}
                      onClick={() =>
                        updateParams({ page: String(currentPage + 1) }, false)
                      }
                      className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/70 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-lg"></i>
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                  <i className="ri-search-eye-line text-2xl"></i>
                </span>
                <p className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  暂无符合条件的影片
                </p>
                <p className="mt-2 max-w-sm text-sm text-foreground-600">
                  试试放宽筛选条件，或换一个关键词再找找看。
                </p>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-refresh-line text-base"></i>
                  清除筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-foreground-950/40"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-xs overflow-y-auto bg-background-50 p-4 animate-soft-fade">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-lg font-semibold text-foreground-950">
                筛选影片
              </h2>
              <button
                type="button"
                aria-label="关闭筛选"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <div className="mt-4">{filterPanel}</div>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-4 w-full whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看筛选结果
            </button>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}